import { ReactElement } from 'react';
import { EnhancedOption } from 'common/components/EnhancedOption/EnhancedOption';
import { EnhancedSelectOption, EnhancedSelectOptions } from 'common/components/EnhancedSelect/EnhancedSelect';
import { filterByValueOrLabel } from 'common/utils/form.utils';
import { formatNumberWithSpace } from 'common/utils/format.utils';
import {
  TAccountDetailRes,
  TAccountEntity,
  AccountKindToFancy,
  TAccountCustomerAssetsEntity,
} from 'domains/accounts/types/accounts.types';
import { formatIfIBAN } from 'domains/accounts/utils/render-account-iban';
import { TCounterpartyEntity } from 'domains/counterparties/types/counterparty.types';
import { TRecipientInitialData, TSenderInitialData } from 'domains/payments/types/payment.types';
import { ERecipientActionTypes, ESenderActionTypes } from '../enums/payment-operation.enum';

export function reducerSender(state: TSenderInitialData, action: any): any {
  switch (action.type) {
    case ESenderActionTypes.SenderOnClear: {
      return {
        ...state,
        senderValue: '',
        searchSenderValue: '',
        senderAccountValue: '',
        isSenderCustomerSelected: false,
      };
    }
    case ESenderActionTypes.SenderAsset: {
      return {
        ...state,
        senderAsset: action.payload,
      };
    }
    case ESenderActionTypes.SenderAccountData: {
      return {
        ...state,
        senderAccountData: action.payload,
      };
    }
    case ESenderActionTypes.SenderAccountValue: {
      return {
        ...state,
        senderAccountValue: action.payload,
      };
    }
    case ESenderActionTypes.SelectSenderAccountData: {
      return {
        ...state,
        selectSenderAccountsData: action.payload,
      };
    }
    case ESenderActionTypes.SelectSenderAccountDataOnClear: {
      return {
        ...state,
        senderAccountValue: '',
        searchSenderValue: '',
      };
    }
    case ESenderActionTypes.SearchSenderValue: {
      return {
        ...state,
        searchSenderValue: action.payload,
      };
    }
    case ESenderActionTypes.SenderCounterpartyOnClear: {
      return {
        ...state,
        senderValue: null,
        isSenderCustomerSelected: false,
        searchSenderCounterpartyValue: '',
      };
    }
    case ESenderActionTypes.SenderDetailAccountsOnChange: {
      return {
        ...state,
        senderAccountValue: action.payload,
      };
    }
    case ESenderActionTypes.SenderCustomerSelect: {
      return {
        ...state,
        isSenderCustomerSelected: action.payload,
      };
    }
    case ESenderActionTypes.SenderBalanceVisible: {
      return {
        ...state,
        senderBalanceVisible: action.payload,
      };
    }
    case ESenderActionTypes.SearchSenderCounterpartyValue: {
      return {
        ...state,
        searchSenderCounterpartyValue: action.payload,
      };
    }
    case ESenderActionTypes.SearchSenderAccountValue: {
      return {
        ...state,
        searchSenderAccountValue: action.payload,
      };
    }
    case ESenderActionTypes.SwapAccounts: {
      return {
        ...state,
        isSenderCustomerSelected: action.payload.isRecipientCustomerSelected,
        senderAccountData: action.payload.recipientAccountData,
        selectSenderAccountsData: action.payload.selectRecipientAccountsData,
        senderDetailAccounts: action.payload.recipientAccountValue,
        senderBalanceVisible: action.payload.recipientBalanceVisible,
      };
    }
    case ESenderActionTypes.Swap: {
      return {
        ...state,
        senderAccountData: action.payload.recipientAccountData,
        senderDetailAccounts: action.payload.recipientDetailAccounts,
        selectSenderAccountsData: action.payload.selectRecipientAccountsData,
        isSenderCustomerSelected: action.payload.isRecipientCustomerSelected,
        senderBalanceVisible: action.payload.recipientBalanceVisible,
      };
    }

    default:
      return state;
  }
  throw Error(`Unknown action: ${action.type}`);
}

export function reducerRecipient(state: TRecipientInitialData, action: any): any {
  switch (action.type) {
    case ERecipientActionTypes.RecipientOnClear: {
      return {
        ...state,
        recipientValue: '',
        searchRecipientValue: '',
        recipientAccountValue: '',
        isRecipientCustomerSelected: false,
      };
    }
    case ERecipientActionTypes.RecipientAsset: {
      return {
        ...state,
        recipientAsset: action.payload,
      };
    }
    case ERecipientActionTypes.RecipientAccountData: {
      return {
        ...state,
        recipientAccountData: action.payload,
      };
    }
    case ERecipientActionTypes.RecipientAccountValue: {
      return {
        ...state,
        recipientAccountValue: action.payload,
      };
    }
    case ERecipientActionTypes.SelectRecipientAccountData: {
      return {
        ...state,
        selectRecipientAccountsData: action.payload,
      };
    }
    case ERecipientActionTypes.SelectRecipientAccountDataOnClear: {
      return {
        ...state,
        recipientAccountValue: '',
        searchRecipientValue: '',
      };
    }
    case ERecipientActionTypes.SearchRecipientValue: {
      return {
        ...state,
        searchRecipientValue: action.payload,
      };
    }
    case ERecipientActionTypes.SearchRecipientAccountValue: {
      return {
        ...state,
        searchRecipientAccountValue: action.payload,
      };
    }
    case ERecipientActionTypes.RecipientCounterpartyOnClear: {
      return {
        ...state,
        recipientValue: null,
        isRecipientCustomerSelected: false,
        searchRecipientCounterpartyValue: '',
      };
    }
    case ERecipientActionTypes.RecipientDetailAccountsOnChange: {
      return {
        ...state,
        recipientAccountValue: action.payload,
      };
    }
    case ERecipientActionTypes.RecipientDetailAccountsOnClear: {
      return {
        ...state,
        recipientAccountValue: '',
        searchRecipientValue: '',
      };
    }
    case ERecipientActionTypes.RecipientCustomerSelect: {
      return {
        ...state,
        isRecipientCustomerSelected: action.payload,
      };
    }
    case ERecipientActionTypes.SwapAccounts: {
      return {
        ...state,
        isRecipientCustomerSelected: action.payload.isSenderCustomerSelected,
        recipientAccountData: action.payload.senderAccountData,
        selectRecipientAccountsData: action.payload.selectSenderAccountsData,
        recipientDetailAccounts: action.payload.senderAccountValue,
        recipientBalanceVisible: false,
      };
    }
    case ERecipientActionTypes.Swap: {
      return {
        ...state,
        recipientAccountData: action.payload.senderAccountData,
        recipientDetailAccounts: action.payload.senderDetailAccounts,
        selectRecipientAccountsData: action.payload.selectSenderAccountsData,
        recipientAccountValue: action.payload.senderAccountValue,
        isRecipientCustomerSelected: action.payload.isSenderCustomerSelected,
      };
    }

    default:
      return state;
  }
  throw Error(`Unknown action: ${action.type}`);
}

export const makeCounterPartyOption = ({ name, guid }: TCounterpartyEntity): { label: string, value: string } => ({
  value: guid,
  label: name,
});

export const makeAccountOption =
  (showBalance: boolean) =>
  (
    {
      wallet_account_guid,
      asset,
      account_kind,
      iban,
    }: TAccountEntity & { asset?: TAccountDetailRes['assets'][number] }
  ): {
    label: string,
    value: string,
    render: () => ReactElement,
} => ({
    value: wallet_account_guid,
    label: iban || wallet_account_guid,
    render: (): ReactElement => (
      <EnhancedOption
        label={formatIfIBAN(iban || wallet_account_guid)}
        layout={0}
        description={AccountKindToFancy[account_kind]}
        secondaryLabel={showBalance && asset ? `${formatNumberWithSpace(asset.balance)} ${asset.stellar_code}` : null}
      />
    ),
  });

export const makeCustomerOption = (
  login: string,
  wallet_user_guid: string,
  showBalance: boolean,
  asset: TAccountCustomerAssetsEntity,
  user_guid: string,
): { label: string, value: string, render: () => ReactElement } => ({
  label: login,
  value: wallet_user_guid,
  render: (): ReactElement => (
    <EnhancedOption
      label={login}
      secondaryLabel={showBalance && user_guid === wallet_user_guid
      && asset ? `${formatNumberWithSpace(asset.balance)} ${asset.stellar_code}` : null}
      layout={1}
      description={wallet_user_guid}
    />
  ),
});

export const makeAccountsOption = (
  name: string,
  account_guid: string,
  iban: string | undefined,
  showBalance: boolean,
  asset: TAccountCustomerAssetsEntity,
): { label: string, value: string, render: () => ReactElement } => ({
  label: iban || account_guid,
  value: account_guid,
  render: (): ReactElement => (
    <EnhancedOption
      label={formatIfIBAN(iban || account_guid)}
      key={account_guid}
      secondaryLabel={showBalance
      && asset ? `${formatNumberWithSpace(asset.balance)} ${asset.stellar_code}` : null}
      layout={1}
      description={name}
    />
  ),
});

export const applyAssetToOption = (
  accounts: TAccountEntity[],
  selected: string | null,
  asset: TAccountDetailRes['assets'][number] | null,
): any => {
  const result = accounts.map((account) =>
    account.wallet_account_guid === selected && asset ? { ...account, asset } : account,
  );
  return result;
};

export const filterRecipientFn = (searchValue: string, options: any, value: string): EnhancedSelectOptions => {
  let filteredOptions: EnhancedSelectOptions = [];

  if (searchValue?.length > 0) {
    filteredOptions = filterByValueOrLabel(options, searchValue).map((option: EnhancedSelectOption) => ({
      ...option,
      secondaryLabel: '',
    }));
  } else {
    filteredOptions = options
      .filter((option: EnhancedSelectOption) => option.value !== value)
      .map((option: EnhancedSelectOption) => ({ ...option, secondaryLabel: '' }));
  }

  return filteredOptions;
};

export const filterFn = (searchValue: string, options: any, value: string): EnhancedSelectOptions => {
  let filteredOptions: EnhancedSelectOptions = [];

  if (searchValue?.length > 0) {
    filteredOptions = filterByValueOrLabel(options, searchValue).map((option: EnhancedSelectOption) => ({
      ...option,
      secondaryLabel: '',
    }));
  } else {
    filteredOptions = options.filter((option: EnhancedSelectOption) => option.value !== value);
  }

  return filteredOptions;
};
